import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { metricsService } from '../../services/metricsService';

export default function RatesTab() {
  const [ratesList, setRatesList] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [editingRate, setEditingRate] = useState(null);

  const fetchRates = async () => {
    setRatesLoading(true);
    try {
      const data = await metricsService.getRates();
      setRatesList(data.rates || []);
    } catch (e) {
      setRatesList([]);
    } finally {
      setRatesLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold">Себестоимость (₽/мин на сервис)</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRates} disabled={ratesLoading}>
            {ratesLoading ? 'Загрузка...' : 'Обновить'}
          </Button>
          <Button size="sm" className="bg-paper-accent hover:bg-paper-accent-hover"
            onClick={() => setEditingRate({ service: '', model: '', rub_per_minute: 0, _isNew: true })}>
            <Plus className="w-4 h-4 mr-1" /> Добавить
          </Button>
        </div>
      </div>
      {ratesList && ratesList.length === 0 && (
        <p className="text-paper-hint text-center py-8">Ставки не заданы</p>
      )}
      {ratesList && ratesList.length > 0 && (
        <div className="grid gap-3">
          {ratesList.map((rate) => (
            <Card key={rate._id} className="p-4">
              <div className="flex justify-between items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{rate.service}
                    {rate.model ? ` / ${rate.model}` : ''}</p>
                  <p className="text-sm text-paper-ink-3">{rate.rub_per_minute} ₽/мин</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setEditingRate({ ...rate })}>
                    Изменить
                  </Button>
                  <button className="text-paper-hint hover:text-paper-accent transition-colors p-1"
                    title="Удалить ставку"
                    onClick={async () => {
                      if (!window.confirm(`Удалить ставку "${rate.service}"?`)) return;
                      try {
                        await metricsService.deleteRate({ service: rate.service, model: rate.model || null });
                        await fetchRates();
                      } catch (err) {
                        alert('Ошибка удаления');
                      }
                    }}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editingRate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
             onClick={() => setEditingRate(null)}>
          <div className="bg-paper-card rounded-card shadow-[0_24px_64px_rgba(0,0,0,.18)] w-full max-w-md mx-4 p-6"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">
              {editingRate._isNew ? 'Добавить ставку' : 'Редактировать ставку'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-paper-ink-2 mb-1">Сервис</label>
                <Input value={editingRate.service}
                  onChange={(e) => setEditingRate({ ...editingRate, service: e.target.value })}
                  placeholder="bit-transcribe-v3" />
              </div>
              <div>
                <label className="block text-sm font-medium text-paper-ink-2 mb-1">Модель (опц.)</label>
                <Input value={editingRate.model || ''}
                  onChange={(e) => setEditingRate({ ...editingRate, model: e.target.value })}
                  placeholder="напр. голос для bit-tts" />
              </div>
              <div>
                <label className="block text-sm font-medium text-paper-ink-2 mb-1">₽/мин</label>
                <Input type="number" step="0.01" value={editingRate.rub_per_minute}
                  onChange={(e) => setEditingRate({ ...editingRate, rub_per_minute: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditingRate(null)}>Отмена</Button>
              <Button className="bg-paper-accent hover:bg-paper-accent-hover"
                disabled={!editingRate.service.trim()}
                onClick={async () => {
                  try {
                    await metricsService.putRate({
                      service: editingRate.service.trim(),
                      model: editingRate.model ? editingRate.model.trim() : null,
                      rub_per_minute: editingRate.rub_per_minute,
                    });
                    setEditingRate(null);
                    await fetchRates();
                  } catch (err) {
                    alert('Ошибка сохранения');
                  }
                }}>
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
